import React, { useEffect, useState } from 'react'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import { scaleQuantile } from 'd3-scale'
import { CircularLoader } from '@st-operations/uikit/src/v2'
import classNames from 'classnames'
import format from '@st-operations/uikit/lib/format.js'

import { CALCULATION_METHODS } from '../../pages/event-feeds/visualization-panel/highlights/geo/index.jsx'
import GeoLegend from '../geo-legend/index.jsx'

import './styles.scss'

function twoDecimals(n) {
  let log10 = n ? Math.floor(Math.log10(n)) : 0,
    div = log10 < 0 ? Math.pow(10, 1 - log10) : 100

  return Math.round(n * div) / div
}

function showTooltip(evt, text) {
  let tooltip = document.getElementById('tooltip')
  tooltip.innerHTML = text
  tooltip.style.display = 'block'
  tooltip.style.left = evt.pageX + 30 + 'px'
  tooltip.style.top = evt.pageY + 30 + 'px'
}

function hideTooltip() {
  let tooltip = document.getElementById('tooltip')
  tooltip.style.display = 'none'
}

const UsZipcodeDistribution = ({ className, calculationMethod, data, loading, type, style }) => {
  let scoreKey
  if (calculationMethod === CALCULATION_METHODS.EVENTS) {
    scoreKey = 'size'
  } else if (calculationMethod === CALCULATION_METHODS.PER_CAPITA) {
    scoreKey = 'perCapitaScore'
  } else if (calculationMethod === CALCULATION_METHODS.INDEX) {
    scoreKey = 'indexScore'
  } else if (calculationMethod === CALCULATION_METHODS.UNIQUE_USERS) {
    scoreKey = 'users'
  }

  const min = 0
  const max = data.reduce((max, cur) => Math.max(max, cur[scoreKey]), 0)

  const minIndexNegative = data.reduce((min, cur) => Math.min(min, cur[scoreKey]), 0)
  const maxIndexPositive = data.reduce((acc, cur) => Math.max(acc, cur[scoreKey]), 0)

  const legendStart = calculationMethod === CALCULATION_METHODS.INDEX ? minIndexNegative : min
  const legendEnd = calculationMethod === CALCULATION_METHODS.INDEX ? maxIndexPositive : max

  const domain = data.map((geo) => geo[scoreKey])

  const colorScale = scaleQuantile()
    .domain(domain)
    .range([
      '#CBD2CD',
      '#B9C7BD',
      '#A6BDAD',
      '#94B29C',
      '#81A88C',
      '#6F9D7C',
      '#5D926C',
      '#4A885C',
      '#387D4B',
      '#25733B',
      '#13682B',
    ])
  const colorScaleIndexNegative = scaleQuantile()
    .domain(domain)
    .range([
      '#904172',
      '#984F7C',
      '#A15D87',
      '#A96B91',
      '#B1799C',
      '#B987A6',
      '#C296B1',
      '#CAA4BB',
      '#D2B2C6',
      '#DAC0D0',
      '#E3CEDB',
      '#EBDCE5',
    ])
  const colorScaleIndexPositive = scaleQuantile()
    .domain(domain)
    .range([
      '#CBD2CD',
      '#B9C7BD',
      '#A6BDAD',
      '#94B29C',
      '#81A88C',
      '#6F9D7C',
      '#5D926C',
      '#4A885C',
      '#387D4B',
      '#25733B',
      '#13682B',
    ])
  const [rendering, setRendering] = useState(true)
  const [geoData, setGeoData] = useState()

  useEffect(() => {
    async function fetchGeoData() {
      const file =
        type === 'counties' ? '/static/geo/counties-10m.json' : '/static/geo/states-10m.json'
      const response = await (await fetch(file)).json()
      setGeoData(response)
    }

    fetchGeoData()
  }, [type])

  return (
    <div className={classNames('st-us-zipcode-distribution', className)} style={style}>
      <div id="tooltip" display="none" style={{ position: 'fixed', display: 'none' }} />

      <ComposableMap projection="geoAlbersUsa">
        <Geographies geography={geoData}>
          {({ geographies }) =>
            geographies.map((geo, index) => {
              const cur = data.find((d) => d.id === geo.id)
              if (index === geographies.length - 1) {
                setRendering(false)
              }
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={
                    cur && cur[scoreKey] !== 1
                      ? calculationMethod === CALCULATION_METHODS.INDEX
                        ? cur[scoreKey] < 1
                          ? colorScaleIndexNegative(cur[scoreKey])
                          : colorScaleIndexPositive(cur[scoreKey])
                        : colorScale(cur[scoreKey])
                      : 'lightgray'
                  }
                  stroke="white"
                  strokeWidth={0.5}
                  onMouseMove={(evt) => showTooltip(evt, geo.properties.name)}
                  onMouseLeave={() => hideTooltip()}
                />
              )
            })
          }
        </Geographies>
      </ComposableMap>
      <GeoLegend
        className="st-us-zipcode-distribution__scale"
        calculationMethod={calculationMethod}
        start={legendStart < 1000 ? twoDecimals(legendStart) : format('metric', legendStart)}
        middle={calculationMethod === CALCULATION_METHODS.INDEX ? 1 : undefined}
        end={legendEnd < 1000 ? twoDecimals(legendEnd) : format('metric', legendEnd)}
      />
      <If condition={rendering || loading}>
        <div className="st-us-zipcode-distribution__loading-container">
          <CircularLoader className="statistics__loader" />
        </div>
      </If>
    </div>
  )
}

export default UsZipcodeDistribution
